<div class="zag-time-range-picker__trigger-wrapper" cdkOverlayOrigin #origin="cdkOverlayOrigin">
  <!-- Start time input -->
  <div class="zag-time-range-picker__input-wrapper" [class.zag-time-range-picker__input-wrapper--error]="startInputError()">
    <input
      #startInput
      [zagScrollIntoView]="true"
      type="text"
      class="zag-time-range-picker__input"
      data-selector-id="time-range-start-input"
      [class.zag-time-range-picker__input--disabled]="disabled()"
      [class.zag-time-range-picker__input--readonly]="readonly()"
      [class.zag-time-range-picker__input--all-selected]="rangeAllSelected()"
      [disabled]="disabled()"
      [readonly]="readonly()"
      [attr.placeholder]="maskInput() ? maskPlaceholder() : (startPlaceholder() | translate)"
      [attr.aria-label]="startPlaceholder() | translate"
      [attr.aria-invalid]="startInputError()"
      [attr.aria-required]="required()"
      [attr.aria-haspopup]="'dialog'"
      [attr.aria-expanded]="open() && (selectionMode() === 'list' || selectingStart())"
      [value]="startInputText()"
      (input)="onStartInputChange($event)"
      (blur)="onStartInputBlur()"
      (focus)="onStartInputFocus()"
      (keydown)="onStartInputKeyDown($event)"
    />
  </div>

  <span class="zag-time-range-picker__separator" aria-hidden="true">–</span>

  <!-- End time input -->
  <div class="zag-time-range-picker__input-wrapper" [class.zag-time-range-picker__input-wrapper--error]="endInputError()">
    <input
      #endInput
      [zagScrollIntoView]="true"
      type="text"
      class="zag-time-range-picker__input"
      data-selector-id="time-range-end-input"
      [class.zag-time-range-picker__input--disabled]="disabled()"
      [class.zag-time-range-picker__input--readonly]="readonly()"
      [class.zag-time-range-picker__input--all-selected]="rangeAllSelected()"
      [disabled]="disabled()"
      [readonly]="readonly()"
      [attr.placeholder]="maskInput() ? maskPlaceholder() : (endPlaceholder() | translate)"
      [attr.aria-label]="endPlaceholder() | translate"
      [attr.aria-invalid]="endInputError()"
      [attr.aria-haspopup]="'dialog'"
      [attr.aria-expanded]="open() && (selectionMode() === 'list' || !selectingStart())"
      [value]="endInputText()"
      (input)="onEndInputChange($event)"
      (blur)="onEndInputBlur()"
      (focus)="onEndInputFocus()"
      (keydown)="onEndInputKeyDown($event)"
    />
    @if (isOvernightRange()) {
    <!-- "+1" indicates the end time is on the following day (overnight shift).
           The value is universal scheduling notation — no translation needed. -->
    <span class="zag-time-range-picker__next-day-badge" aria-label="next day">+1</span>
    }
  </div>

  @if (showClearButton()) {
  <button
    type="button"
    class="zag-time-range-picker__clear"
    [attr.aria-label]="'timePicker.clearTimeRange' | translate"
    (click)="clear($event)"
  >
    <span zagIcon="close"></span>
  </button>
  }
</div>

<ng-template
  cdkConnectedOverlay
  [cdkConnectedOverlayOrigin]="origin"
  [cdkConnectedOverlayOpen]="open()"
  [cdkConnectedOverlayPositions]="isMobile() ? mobileOverlayPositions : overlayPositions"
  [cdkConnectedOverlayFlexibleDimensions]="true"
  [cdkConnectedOverlayViewportMargin]="8"
  [cdkConnectedOverlayPush]="true"
  [cdkConnectedOverlayGrowAfterOpen]="true"
  [cdkConnectedOverlayHasBackdrop]="true"
  [cdkConnectedOverlayBackdropClass]="isMobile() ? 'zag-picker-dialog-backdrop' : 'cdk-overlay-transparent-backdrop'"
  [cdkConnectedOverlayPanelClass]="isMobile() ? 'zag-picker-dialog-panel' : ''"
  (backdropClick)="closeAndRollback()"
  (detach)="closeAndRollback()"
>
  <dialog
    class="zag-time-range-picker-panel"
    open
    tabindex="-1"
    aria-modal="false"
    [attr.aria-label]="'timePicker.selectTimeRange' | translate"
    (keydown)="onPanelKeyDown($event)"
  >
    @if (isMobile()) {
    <div class="zag-picker-mobile-header">
      <span class="zag-picker-mobile-header__label">{{ fieldLabel() || ('timePicker.selectTimeRange' | translate) }}</span>
      <button type="button" class="zag-picker-mobile-header__close" [attr.aria-label]="'picker.close' | translate" (click)="close()">
        <span zagIcon="close"></span>
      </button>
    </div>
    } @if (selectionMode() === 'columns') {
    <!-- Selecting start / end indicator -->
    <div class="zag-time-range-picker-panel__targets">
      <button
        type="button"
        class="zag-time-range-picker-panel__target"
        [class.zag-time-range-picker-panel__target--active]="selectingStart()"
        (click)="setSelectionTarget(true)"
      >
        {{ start() ? toDisplayTime(start()) : (startPlaceholder() | translate) }}
      </button>
      <button
        type="button"
        class="zag-time-range-picker-panel__target"
        [class.zag-time-range-picker-panel__target--active]="!selectingStart()"
        [disabled]="!start()"
        (click)="setSelectionTarget(false)"
      >
        {{ end() ? toDisplayTime(end()) : (endPlaceholder() | translate) }}
      </button>
    </div>
    }

    <div
      class="zag-time-range-picker-panel__controls"
      [class.zag-time-range-picker-panel__controls--list]="selectionMode() === 'list'"
      [attr.aria-label]="'timePicker.selectTime' | translate"
    >
      @if (selectionMode() === 'list') {
      <div class="zag-time-range-picker-panel__lists">
        <div class="zag-time-range-picker-panel__list">
          <span class="zag-time-range-picker-panel__list-label">{{ startPlaceholder() | translate }}</span>
          <div
            class="scroll-column scroll-column--list"
            data-selector-id="time-range-start-list"
            role="listbox"
            [attr.aria-label]="startPlaceholder() | translate"
          >
            @for (t of allTimeOptions(); track t) {
            <button
              type="button"
              class="scroll-column__item"
              [class.scroll-column__item--selected]="isTimeOptionSelected(t, true)"
              [class.scroll-column__item--disabled]="isTimeOptionDisabled(t, true)"
              role="option"
              [attr.aria-selected]="isTimeOptionSelected(t, true)"
              [attr.aria-disabled]="isTimeOptionDisabled(t, true)"
              [attr.aria-label]="(startPlaceholder() | translate) + ' ' + toListDisplayTime(t)"
              [disabled]="isTimeOptionDisabled(t, true)"
              (click)="selectTimeOption(t, true)"
            >
              {{ toListDisplayTime(t) }}
            </button>
            }
          </div>
        </div>

        <div class="zag-time-range-picker-panel__list">
          <span class="zag-time-range-picker-panel__list-label">{{ endPlaceholder() | translate }}</span>
          <div
            class="scroll-column scroll-column--list"
            data-selector-id="time-range-end-list"
            role="listbox"
            [attr.aria-label]="endPlaceholder() | translate"
          >
            @for (t of allTimeOptions(); track t) {
            <button
              type="button"
              class="scroll-column__item"
              [class.scroll-column__item--selected]="isTimeOptionSelected(t, false)"
              [class.scroll-column__item--disabled]="isTimeOptionDisabled(t, false)"
              role="option"
              [attr.aria-selected]="isTimeOptionSelected(t, false)"
              [attr.aria-disabled]="isTimeOptionDisabled(t, false)"
              [attr.aria-label]="(endPlaceholder() | translate) + ' ' + toListDisplayTime(t)"
              [disabled]="isTimeOptionDisabled(t, false)"
              (click)="selectTimeOption(t, false)"
            >
              {{ toListDisplayTime(t) }}
            </button>
            }
          </div>
        </div>
      </div>
      } @else {
      <!-- Hours scroll column (looped for infinite scroll when enough items) -->
      <div
        class="scroll-column"
        [class.scroll-column--looped]="isHoursLooped()"
        role="listbox"
        [attr.aria-label]="'timePicker.hours' | translate"
        (scroll)="onColumnScroll($event)"
      >
        @for (h of loopedHours(); track $index) {
        <button
          type="button"
          class="scroll-column__item"
          [class.scroll-column__item--selected]="isHourSelected(h)"
          [class.scroll-column__item--disabled]="isHourDisabled(h)"
          role="option"
          [attr.aria-selected]="isHourSelected(h)"
          [attr.aria-disabled]="isHourDisabled(h)"
          [attr.aria-label]="('timePicker.hours' | translate) + ' ' + displayHourFor(h)"
          [disabled]="isHourDisabled(h)"
          (click)="selectHour(h % 24)"
        >
          {{ displayHourFor(h) }}
        </button>
        }
      </div>

      <span class="time-separator--range" aria-hidden="true">:</span>

      <!-- Minutes scroll column (looped for infinite scroll when enough items) -->
      <div
        class="scroll-column"
        [class.scroll-column--looped]="isMinutesLooped()"
        role="listbox"
        [attr.aria-label]="'timePicker.minutes' | translate"
        (scroll)="onColumnScroll($event)"
      >
        @for (m of loopedMinutes(); track $index) {
        <button
          type="button"
          class="scroll-column__item"
          [class.scroll-column__item--selected]="isMinuteSelected(m)"
          [class.scroll-column__item--disabled]="isMinuteDisabled(m)"
          role="option"
          [attr.aria-selected]="isMinuteSelected(m)"
          [attr.aria-disabled]="isMinuteDisabled(m)"
          [attr.aria-label]="('timePicker.minutes' | translate) + ' ' + m"
          [disabled]="isMinuteDisabled(m)"
          (click)="selectMinute(m % 60)"
        >
          {{ m | number: '2.0' }}
        </button>
        }
      </div>

      @if (showSeconds()) {
      <span class="time-separator--range" aria-hidden="true">:</span>

      <!-- Seconds scroll column (always looped — 60 items) -->
      <div
        class="scroll-column scroll-column--looped"
        role="listbox"
        [attr.aria-label]="'timePicker.seconds' | translate"
        (scroll)="onColumnScroll($event)"
      >
        @for (s of loopedSeconds; track $index) {
        <button
          type="button"
          class="scroll-column__item"
          [class.scroll-column__item--selected]="isSecondSelected(s)"
          role="option"
          [attr.aria-selected]="isSecondSelected(s)"
          [attr.aria-label]="('timePicker.seconds' | translate) + ' ' + s"
          (click)="selectSecond(s % 60)"
        >
          {{ s | number: '2.0' }}
        </button>
        }
      </div>
      } @if (format() === '12h') {
      <!-- Period scroll column -->
      <div class="scroll-column scroll-column--period" role="listbox" [attr.aria-label]="'timePicker.period' | translate">
        @for (p of periods; track p) {
        <button
          type="button"
          class="scroll-column__item"
          [class.scroll-column__item--selected]="isPeriodSelected(p)"
          role="option"
          [attr.aria-selected]="isPeriodSelected(p)"
          [attr.aria-label]="p"
          (click)="selectPeriod(p)"
        >
          {{ p }}
        </button>
        }
      </div>
      } }
    </div>

    <div class="zag-time-range-picker-panel__actions">
      @if (selectionMode() === 'list') {
      <button
        type="button"
        class="zag-time-range-picker-panel__action zag-time-range-picker-panel__action--primary"
        [disabled]="!canConfirmListSelection()"
        (click)="confirmListSelection()"
      >
        {{ 'timePicker.set' | translate }}
      </button>
      } @else {
      <button type="button" class="zag-time-range-picker-panel__action zag-time-range-picker-panel__action--secondary" (click)="setNow()">
        {{ 'timePicker.now' | translate }}
      </button>
      <button
        type="button"
        class="zag-time-range-picker-panel__action zag-time-range-picker-panel__action--primary"
        [disabled]="!canApplyCurrent()"
        (click)="applySelection()"
      >
        {{ selectingStart() ? ('timePicker.setStart' | translate) : ('timePicker.setEnd' | translate) }}
      </button>
      }
    </div>
  </dialog>
</ng-template>
